import { useEffect } from 'react';
import useStore from 'state/store/hooks/useStore';
import { useOrgLevel } from './useOrgLevel';

/**
 * Custom hook to get store name from entity_id
 * Automatically fetches stores list if needed when in org-level view
 *
 * @returns Object with getStoreName function and storesList
 *
 * @example
 * ```tsx
 * const { getStoreName } = useStoreName();
 *
 * // In render
 * <div>{getStoreName(row)}</div>
 * ```
 */
export const useStoreName = () => {
  const { store, toFetchStoresList } = useStore();
  const isOrgLevel = useOrgLevel();

  // Auto-fetch stores list if not loaded and in org view
  useEffect(() => {
    if (isOrgLevel && !store.storesList) {
      toFetchStoresList();
    }
  }, [isOrgLevel, store.storesList, toFetchStoresList]);

  /**
   * Get store name from a row object containing entity_id or store_name
   */
  const getStoreName = (row: {
    entity_id?: string;
    store_name?: string;
    storeName?: string;
  }): string => {
    if (!store.storesList) return '-';

    // Map entity_id to entity_id in storesList
    if (row.entity_id) {
      const storeData = store.storesList.find(
        (s) => s.entity_id === row.entity_id
      );
      if (storeData) return storeData.dba_name;
    }

    // Fallback to store_name if directly in response
    const storeName = row.store_name || row.storeName;
    if (storeName) {
      return storeName;
    }

    return '-';
  };

  return {
    getStoreName,
    storesList: store.storesList,
    isLoading: store.isLoading,
  };
};

export default useStoreName;
